import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartChange);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#removeDiscoverMoreIfDisabled);
    document.addEventListener(CartUpdateEvent.eventName, this.#removeDiscoverMoreIfDisabled);
    this.#removeDiscoverMoreIfDisabled();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartUpdateEvent.eventName, this.#handleCartChange);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#removeDiscoverMoreIfDisabled);
    document.removeEventListener(CartUpdateEvent.eventName, this.#removeDiscoverMoreIfDisabled);
  }

  #removeDiscoverMoreIfDisabled = () => {
    if (this.dataset.showDiscoverMore !== 'false') return;

    this.querySelectorAll('cart-drawer-discover-more').forEach((el) => el.remove());
    this.querySelectorAll('#cart-drawer-discover-more-root').forEach((root) => {
      root.closest('.cart-drawer-discover')?.remove();
    });
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    this.#scheduleStickyStateUpdate();
  };

  #handleCartChange = () => {
    this.#scheduleStickyStateUpdate();
  };

  /** Recompute layout after section morph (empty→filled cart, quantity changes). */
  #scheduleStickyStateUpdate() {
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#updateStickyState();
    });
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Empty or transitional markup: avoid split-scroll layout.
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Line items use natural height; dialog scrolls as a whole (summary + strips below long carts).
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
